import {
  Shield, FileText, BarChart3, Clock, ChevronRight, CheckCircle,
  TrendingUp, AlertTriangle, Zap,
} from 'lucide-react';
import GlowCard from './components/GlowCard';
import KPITile from './components/KPITile';
import WorkflowGraphic from './components/WorkflowGraphic';

interface OverviewProps {
  onNavigate?: (key: string) => void;
}

const contextRows: [string, string][] = [
  ['Project', 'Viking CCS – Demo'],
  ['Client', 'Confidential Energy Operator'],
  ['Disciplines', 'Piping / Mechanical / Process'],
  ['Phase', 'Define / Execute'],
  ['Deliverables', 'P&ID, Datasheet, Vendor Pkg'],
  ['Standards', 'AGPS, ISO, API, Client Rules'],
  ['Review Engine', 'Deterministic + AI Hybrid'],
  ['Status', 'Active Review Cycle'],
];

const whyItMatters = [
  { icon: Shield, title: 'Governance & Auditability', desc: 'Every finding is linked to a clause reference, evidence location, and reviewer action — delivering audit-ready assurance.' },
  { icon: Zap, title: 'Accelerated First-Pass Review', desc: 'Reduce manual review burden with deterministic + AI hybrid checks while preserving engineering judgement where it matters.' },
  { icon: BarChart3, title: 'Operational Transparency', desc: 'Real-time dashboards showing review cycle performance, contractor compliance trends, and recurring non-compliance themes.' },
];

/** Overview / landing page — faithful reproduction of the IQ Verity prototype Landing. */
const VerityOverview = ({ onNavigate }: OverviewProps) => {
  const go = (key: string) => () => onNavigate?.(key);

  return (
    <div className="vr-page">
      {/* Hero */}
      <section className="vr-hero vr-glass vr-fade-in">
        <div className="vr-hero-wash" />
        <div className="vr-hero-inner">
          <div className="vr-chip vr-fade-in vr-d1" style={{ marginBottom: '1rem' }}>
            <Zap size={12} /> Proof of Concept — Demo Environment
          </div>
          <h1 className="vr-hero-title vr-fade-in vr-d2">
            AI-Enabled Engineering <span className="vr-text-gradient">Deliverables Review</span>
          </h1>
          <p className="vr-hero-sub vr-fade-in vr-d3">
            Automated first-pass standards assurance combining deterministic rules, AI-assisted document
            understanding, and grounded reasoning — with full traceability, auditability, and human
            engineering oversight.
          </p>
          <div className="vr-hero-actions vr-fade-in vr-d4">
            <button className="vr-btn vr-btn-lg vr-btn-glow" onClick={go('projects')}>
              View Demo Workflow <ChevronRight size={16} />
            </button>
            <button className="vr-btn vr-btn-lg vr-btn-outline" onClick={go('projects')}>
              Review Findings
            </button>
            <button className="vr-btn vr-btn-lg vr-btn-ghost" onClick={go('dashboard')}>
              Open Dashboards
            </button>
            <button className="vr-btn vr-btn-lg vr-btn-ghost" onClick={go('projects')}>
              View Audit Trail
            </button>
          </div>
        </div>
      </section>

      {/* KPIs */}
      <div className="vr-grid vr-grid-4">
        <div className="vr-fade-in vr-d1"><KPITile icon={FileText} label="Deliverables Reviewed" value="247" trend="+18%" trendUp /></div>
        <div className="vr-fade-in vr-d2"><KPITile icon={CheckCircle} label="Compliance Rate" value="91.3%" trend="+2.4%" trendUp /></div>
        <div className="vr-fade-in vr-d3"><KPITile icon={AlertTriangle} label="Open Findings" value="34" trend="-12%" trendUp /></div>
        <div className="vr-fade-in vr-d4"><KPITile icon={TrendingUp} label="Avg. Review Time" value="4.2h" trend="-38%" trendUp /></div>
      </div>

      {/* Workflow */}
      <section>
        <h2 className="vr-h-sec" style={{ marginBottom: '1.5rem' }}>Platform Workflow</h2>
        <GlowCard style={{ padding: '2rem' }}>
          <WorkflowGraphic />
        </GlowCard>
      </section>

      {/* Why it matters */}
      <section>
        <h2 className="vr-h-sec" style={{ marginBottom: '1.5rem' }}>Why It Matters</h2>
        <div className="vr-grid vr-grid-3">
          {whyItMatters.map((item) => (
            <GlowCard key={item.title} className="vr-feature">
              <div className="vr-feature-icon"><item.icon size={20} /></div>
              <h3>{item.title}</h3>
              <p>{item.desc}</p>
            </GlowCard>
          ))}
        </div>
      </section>

      {/* Project context */}
      <section>
        <GlowCard style={{ padding: '1.5rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.75rem', marginBottom: '1rem' }}>
            <Clock size={16} style={{ color: 'hsl(var(--vr-primary))' }} />
            <h3 style={{ fontFamily: 'Space Grotesk', fontWeight: 600 }}>Demo Project Context</h3>
          </div>
          <div className="vr-context-grid">
            {contextRows.map(([label, value]) => (
              <div key={label}>
                <span className="vr-muted">{label}:</span>{' '}
                <span style={{ fontWeight: 500 }}>{value}</span>
              </div>
            ))}
          </div>
        </GlowCard>
      </section>
    </div>
  );
};

export default VerityOverview;
